import React, { FC, useState, useEffect } from "react";
import { Add, Remove } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import ProductImage from "../../../common/ProductImage/ProductImage";
import { trackEcommerceEvent } from "../../../../hooks/useGTM";
import {
  calculateBasePrice,
  calculateOptionsSurcharge,
} from "../../../../utils/cartCalculationUtils";
import styles from "./MiniCartItem.module.css";

interface Props {
  item: any;
  handleIncrease: () => void;
  handleDecrease: () => void;
  handleRemove: () => void;
  getOptimisticQuantity: (defaultQty: number) => number;
  getOptimisticPrice: (defaultPrice: number) => number;
  getOptimisticTotalPrice: (defaultPrice: number, qty: number) => number;
  toggleSidenav: () => void;
  handleRemoveOptimistic?: () => void;
}

const MiniCartItem: FC<Props> = ({
  item,
  handleIncrease,
  handleDecrease,
  handleRemove,
  getOptimisticQuantity,
  getOptimisticPrice,
  getOptimisticTotalPrice,
  toggleSidenav,
  handleRemoveOptimistic,
}) => {
  const navigate = useNavigate();
  const [isRemoving, setIsRemoving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [previousQuantity, setPreviousQuantity] = useState(item.quantity);

  // Get the optimistic quantity for this item
  const quantity = getOptimisticQuantity(item.quantity);

  // Effect to detect quantity changes for animation purposes
  useEffect(() => {
    if (quantity !== previousQuantity && !isRemoving) {
      setIsUpdating(true);
      const timer = setTimeout(() => setIsUpdating(false), 500);

      setPreviousQuantity(quantity);
      return () => clearTimeout(timer);
    }
  }, [quantity, previousQuantity, isRemoving]);

  // Immediately filter out items with quantity 0
  if (quantity <= 0 && !item.isOptimistic) {
    return null;
  }

  // Use utility functions for price calculations
  const basePrice = calculateBasePrice(item);
  const priceSurcharge = calculateOptionsSurcharge(item);

  // Get optimistic total price
  const totalLinePrice = getOptimisticTotalPrice(
    basePrice + priceSurcharge,
    quantity
  );

  // Handle edit click to modify product options
  const handleEditClick = () => {
    if (!item.product?.url) {
      console.error("❌ Product URL is missing");
      return;
    }

    navigate(`/p/${item.product.url}`, {
      state: {
        groupingAttributes: item.groupingAttributes,
        customizableOptions: item.customizableOptions,
      },
    });
    toggleSidenav();
  };

  // Determine if this item is an optimistic update
  const isOptimistic = item.isOptimistic === true;

  // Check if optimistic update is taking too long (> 10 seconds)
  const isSlowOptimistic =
    isOptimistic && item.optimisticAge && item.optimisticAge > 10;

  // Is the current quantity different from the original item quantity?
  const hasOptimisticChange = quantity !== item.quantity;

  // Get product URL, handling potential missing data
  const productUrl = item.product?.url || "";

  // For items that are being updated, add a visual indicator
  const itemClasses = `${styles.cartItem} 
    ${isOptimistic ? styles.optimistic : ""} 
    ${isRemoving ? styles.removing : ""} 
    ${isUpdating ? styles.updating : ""}
    ${isSlowOptimistic ? styles.slowOptimistic : ""}
    ${hasOptimisticChange ? styles.optimisticChange : ""}`;

  const onDecrease = () => {
    if (isOptimistic || isRemoving) return;

    if (quantity <= 1) {
      setIsRemoving(true);

      // Track remove from cart event
      trackEcommerceEvent("remove_from_cart", {
        currency: "PEN",
        value: totalLinePrice,
        items: [
          {
            item_id: item.product?.id,
            item_name: item.product?.name || "Unknown Product",
            price: basePrice,
            quantity: quantity,
          },
        ],
      });

      // Short delay for animation
      setTimeout(() => {
        handleRemove();
      }, 200);
    } else {
      // Track remove item event (decrease quantity)
      trackEcommerceEvent("remove_item", {
        currency: "PEN",
        value: basePrice + priceSurcharge,
        items: [
          {
            item_id: item.product?.id,
            item_name: item.product?.name || "Unknown Product",
            price: basePrice,
            quantity: 1,
          },
        ],
      });

      handleDecrease();
    }
  };

  const onIncrease = () => {
    if (isOptimistic || isRemoving) return;

    // Check product stock limit
    const availableStock = item.product?.stock || 0;
    if (quantity >= availableStock) return;

    // Track add item event (increase quantity)
    trackEcommerceEvent("add_item", {
      currency: "PEN",
      value: basePrice + priceSurcharge,
      items: [
        {
          item_id: item.product?.id,
          item_name: item.product?.name || "Unknown Product",
          price: basePrice,
          quantity: 1,
        },
      ],
    });

    handleIncrease();
  };

  return (
    <div className={itemClasses}>
      {productUrl ? (
        <Link
          to={`/p/${productUrl}`}
          className={styles.productImageContainer}
          onClick={
            isOptimistic || hasOptimisticChange
              ? (e) => e.preventDefault()
              : undefined
          }
        >
          <ProductImage
            alt={item.product?.name || "Product Image"}
            src={
              item.product?.images?.[0]?.thumbnail ||
              item.product?.images?.[0]?.small ||
              item.product?.parent?.images?.[0]?.thumbnail
            }
            className={styles.productImage}
          />
        </Link>
      ) : (
        <div className={styles.productImageContainer}>
          <ProductImage alt="Product Image" className={styles.productImage} />
        </div>
      )}

      <div className={styles.productDetails}>
        {productUrl ? (
          <Link
            to={`/p/${productUrl}`}
            className={styles.productName}
            onClick={
              isOptimistic || hasOptimisticChange
                ? (e) => e.preventDefault()
                : undefined
            }
          >
            {item.product.name}
          </Link>
        ) : (
          <span className={styles.productName}>
            {item.product?.name || "Product"}
          </span>
        )}

        <div className={styles.priceContainer}>
          <p className={styles.productPrice}>
            S/ {isNaN(basePrice) ? "0.00" : basePrice.toFixed(2)}
          </p>
          {priceSurcharge > 0 && (
            <p className={styles.productAdditionalPrice}>
              + S/ {priceSurcharge.toFixed(2)} adicionales
            </p>
          )}
          <p
            className={`${styles.totalPrice} ${
              isOptimistic || hasOptimisticChange ? styles.optimisticValue : ""
            }`}
          >
            Total: S/{" "}
            {isNaN(totalLinePrice) ? "0.00" : totalLinePrice.toFixed(2)}
          </p>
        </div>

        {item.customizableOptions?.length > 0 &&
          !isOptimistic &&
          !hasOptimisticChange && (
            <button className={styles.editButton} onClick={handleEditClick}>
              Editar opciones
            </button>
          )}
      </div>

      <div className={styles.qtyControls}>
        <button
          className={styles.qtyButton}
          onClick={onDecrease}
          disabled={isOptimistic || isRemoving}
          aria-label="Decrease quantity"
        >
          <Remove fontSize="small" />
        </button>
        <span
          className={`${styles.qtyText} ${
            hasOptimisticChange ? styles.optimisticQty : ""
          } ${isUpdating ? styles.updating : ""}`}
        >
          {quantity}
        </span>
        <button
          className={styles.qtyButton}
          onClick={onIncrease}
          disabled={
            isOptimistic || isRemoving || quantity >= (item.product?.stock || 0)
          }
          aria-label="Increase quantity"
        >
          <Add fontSize="small" />
        </button>
      </div>
    </div>
  );
};

export default MiniCartItem;
